import { useAtomValue } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useTenantUsers from "../tenantUsers/useTenantUsers";
import useUser from "../auth/useUser";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import WorkspaceAPI from "../../api/WorkspaceAPI";
import { handleWorkspacesUserUpdate } from "../../../utils/WorkspaceUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

const useDeleteWorkspaceUser = () => {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const { wPermissions } = useWorkspacePermissions();
  const { wPermissions: workspacePermissions } = useUser();
  const { users } = useTenantUsers();
  const queryClient = useQueryClient();

  const deleteWorkspaceUser = useMutation({
    mutationFn: ({
      userID,
      onSuccess,
    }: {
      userID: string;
      onSuccess?: () => void;
    }) => {
      if (!wPermissions.workspace_delete_user) {
        return Promise.reject(
          "User does not have permission to delete users in this workspace"
        );
      }

      return WorkspaceAPI.deleteWorkspaceUser(workspaceID, userID).then(
        (data) => {
          handleWorkspacesUserUpdate(
            data,
            queryClient,
            workspaceID || "",
            workspacePermissions,
            users,
            tenantID || ""
          );

          callFunctionIfExists(onSuccess);
        }
      );
    },
  });

  return {
    deleteWorkspaceUser: deleteWorkspaceUser.mutate,
    deletingWorkspaceUser: deleteWorkspaceUser.isPending,
    deleteWorkspaceUserError: deleteWorkspaceUser.error
      ? getErrorMessage(deleteWorkspaceUser.error)
      : null,
  };
};

export default useDeleteWorkspaceUser;
